import {CTA, ThemeProvider, styled} from '@volkswagen-onehub/components-core';
import {Pause, Play} from '@volkswagen-onehub/icons-core';
import * as React from 'react';
import {css, keyframes} from 'styled-components';

const circleFill = keyframes`
0% {
    clip-path: polygon(100% 0%, 100% 0%, 50% 50%, 100% 0%, 100% 0%, 100% 0);
}

25% {
    clip-path: polygon(0% 0%, 100% 0%, 50% 50%, 0% 0%, 0% 0%, 0% 0%);
}

50% {
    clip-path: polygon(0% 0%, 100% 0%, 50% 50%, 0% 100%, 0% 100%, 0% 100%);
}

75% {
    clip-path: polygon(0% 0%, 100% 0%, 50% 50%, 100% 100%, 100% 100%, 0% 100%);
}

100% {
    clip-path: polygon(0% 0%, 100% 0%, 50% 50%, 100% 0%, 100% 100%, 0% 100%);
}
`;

type TimerProps = Readonly<{
    timeout: number;
    isRotating: boolean;
    disabled: boolean;
}>;

const StyledTimer = styled.div<TimerProps>`
    position: absolute;
    height: var(--button-dimensions);
    width: var(--button-dimensions);
    border: 1px solid ${props => props.theme.colors.button.secondary.default};
    border-radius: 50%;
    clip-path: polygon(0% 0%, 100% 0%, 50% 50%, 100% 0%, 100% 100%, 0% 100%);
    transform: rotate(45deg) scaleX(-1); // polygon starts in top/left corner therefore needs to be rotated and mirrored to start in the top center and move clockwise

    &.animate {
        ${props =>
            props.isRotating &&
            css`
                animation: ${circleFill} ${props.timeout}s forwards linear;
            `}
    }

    ${props =>
        props.disabled &&
        `border-color: ${props.theme.colors.button.secondary.disabled}`}
`;

const StyledButtonWrapper = styled.div<{
    disabled: boolean;
    isMobileAndRtl: boolean;
}>`
    --button-dimensions: ${props => props.theme.size.static500};

    position: relative;
    padding-block-start: ${props => props.theme.size.static750};
    grid-row: row1;

    ${props =>
        !props.disabled &&
        `&:hover {
            & ${StyledTimer} {
                border-color: ${props.theme.colors.button.secondary.active};
            }
        }`}

    &:before {
        content: '';
        position: absolute;
        height: var(--button-dimensions);
        width: var(--button-dimensions);
        background: ${props => props.theme.colors.overlay.default};
        border-radius: 50%;
    }
`;

const PlayPauseIcon = (props: {isRotating: boolean}): React.ReactElement =>
    props.isRotating ? (
        <Pause aria-hidden variant="default" />
    ) : (
        <Play aria-hidden variant="default" />
    );

export type AnimatedAutoScrollButtonProps = Readonly<{
    rotationBtnAriaLabel: string;
    playAriaLabel: string;
    pauseAriaLabel: string;
    isRotating: boolean;
    disabled?: boolean;
    timeout: number;
    handleClick: () => void;
    isMobileAndRtl: boolean;
}>;

export const AnimatedAutoScrollButton = React.forwardRef<
    HTMLDivElement,
    AnimatedAutoScrollButtonProps
>((props, ref) => {
    const {
        rotationBtnAriaLabel,
        playAriaLabel,
        pauseAriaLabel,
        isRotating,
        disabled = false,
        timeout,
        handleClick,
        isMobileAndRtl
    } = props;

    const ariaLabel = `${rotationBtnAriaLabel} ${
        isRotating ? pauseAriaLabel : playAriaLabel
    }`;

    return (
        <ThemeProvider theme="inverted">
            <StyledButtonWrapper
                disabled={disabled}
                isMobileAndRtl={isMobileAndRtl}
            >
                <StyledTimer
                    aria-hidden
                    timeout={timeout}
                    isRotating={isRotating}
                    disabled={disabled}
                    ref={ref}
                    className="animate"
                />
                <CTA
                    icon={<PlayPauseIcon isRotating={isRotating} />}
                    tag="button"
                    type="button"
                    key={ariaLabel}
                    onClick={handleClick}
                    emphasis="tertiary"
                    aria-label={ariaLabel}
                    size="large"
                    disabled={disabled}
                />
            </StyledButtonWrapper>
        </ThemeProvider>
    );
});
